import { Modal, Breadcrumb, Descriptions, Popconfirm } from "antd";
import { HomeOutlined, FolderOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import ReportBreakDown from "./ReportBreakDown";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import { updateRootPageRenderState } from "../../store/slices/projectManagement/project-view/ProjectInDetail";
import { resetReportInDetailModalSlice, updateReportInDetailModalSlice } from "../../store/slices/expenses/ReportInDetailView";
import { expensesApis } from "../../api/Expenses";
import { useState, useEffect } from "react";

const ReportInDetailModal = () => {
  const dispatch = useDispatch();
  const [createdByName, setCreatedByName] = useState<string>("");
  const [freshReportData, setFreshReportData] = useState<any>(null);

  const { data: ReportInDetailModal } = useSelector(
    (state: RootState) => state.ReportInDetailModal
  );
  const { id, open, reportName, projectResponse, activeTab, extra, refreshReportBreakDown, sourcePage } =
    ReportInDetailModal as any;
  const { id: projectId, name: projectName } = projectResponse || {};

  const { data: RootPageRenderState } = useSelector(
    (state: RootState) => state.RootPageRenderState
  );

  // Breadcrumb Navigation: Handle breadcrumb click to return to project view
  const handleProjectBreadcrumbClick = () => {
    dispatch(updateRootPageRenderState(!RootPageRenderState)); // call when exiting to root page
    dispatch(resetReportInDetailModalSlice());
  };

  // Data Integration: Fetch report data when modal opens and data is incomplete
  useEffect(() => {
    const fetchReportData = async () => {
      if (open && id && projectId && (!extra?.createdByName && !extra?.createdBy)) {
        try {
          const response = await expensesApis.fetchReport(projectId, parseInt(id));
          if (response?.status && response.data) {
            const reportData = response.data;
            setCreatedByName(reportData.createdByName || reportData.createdBy?.toString() || "--");
          }
        } catch (error) {
          setCreatedByName("--");
        }
      } else if (open && extra?.createdByName) {
        setCreatedByName(extra.createdByName);
      } else if (open && extra?.createdBy) {
        try {
          const response = await expensesApis.fetchReport(projectId, parseInt(id));
          if (response?.status && response.data?.createdByName) {
            setCreatedByName(response.data.createdByName);
          } else {
            setCreatedByName(extra.createdBy.toString());
          }
        } catch (error) {
          setCreatedByName(extra.createdBy.toString());
        }
      } else {
        setCreatedByName("--");
      }
    };

    fetchReportData();
  }, [open, id, projectId, extra?.createdByName, extra?.createdBy]);


  // Report Breakdown Integration: Convert modal data to report format for ReportBreakDown component
  const reportData = {
    id: id?.toString() || "",
    reportName: reportName || "",
    status: extra?.reportStatus || "",
    submittedDate: extra?.createdAt || "",
    approvedDate: extra?.updatedAt,
    totalAmount: extra?.budgetValue || 0,
    expenses: extra?.expenses || [],
    total: extra?.total || "₹0",
    amountToBeReimbursed: extra?.reimbursableAmount || "₹0",
    submitter: extra?.createdBy,
    dateRange: extra?.startDate && extra?.endDate ? `${extra.startDate} to ${extra.endDate}` : "",
    paginationId: extra?.paginationId,
    createdBy: extra?.createdBy,
    createdByName: createdByName,
    totalIn: extra?.totalIn || 0,
    totalOut: extra?.totalOut || 0,
    balance: extra?.balance || 0,
    reimbursableAmount: extra?.reimbursableAmount || 0,
    nonReimbursableAmount: extra?.nonReimbursableAmount || 0,
    taxAmount: extra?.taxAmount || 0,
    approvalData: extra?.expenseApprovalHierarchy || [],
    expenseReportId: extra?.expenseReportId,
    budgetLineId: extra?.budgetLineId,
    orgId: extra?.orgId,
    projectId: extra?.projectId,
    businessPurpose: extra?.businessPurpose,
    startDate: extra?.startDate,
    endDate: extra?.endDate,
    reportStatus: extra?.reportStatus,
    updatedBy: extra?.updatedBy,
    createdAt: extra?.createdAt,
    updatedAt: extra?.updatedAt,
    budgetValue: extra?.budgetValue,
    approvalRequired: extra?.approvalRequired,
    userTotalIn: extra?.userTotalIn,
    userTotalOut: extra?.userTotalOut,
    userBalance: extra?.userBalance,
    userReimbursableBalance: extra?.userReimbursableBalance,
    userNonReimbursableAmount: extra?.userNonReimbursableAmount,
    userReimbursableTaxAmount: extra?.userReimbursableTaxAmount,
    userTotalAdvances: extra?.userTotalAdvances,
    expenseApprovals: extra?.expenseApprovals || [],
    dataAlreadyFetched: extra?.dataAlreadyFetched || false,
    fetchedReportData: extra?.fetchedReportData,
    fetchedExpensesData: extra?.fetchedExpensesData
  };

  // State Management: Handle refresh completion - reset the refresh flag and trigger parent refresh
  const handleRefreshComplete = () => {
    if (refreshReportBreakDown) {
      dispatch(updateReportInDetailModalSlice({ refreshReportBreakDown: false }));
    }
    
  };

  // Fresh Data Handling: Callback to receive fresh data from ReportBreakDown
  const handleFreshDataUpdate = (freshData: any) => {
    setFreshReportData(freshData);
    
    if (freshData?.createdByName) {
      setCreatedByName(freshData.createdByName);
    } else if (freshData?.createdBy) {
      setCreatedByName(freshData.createdBy.toString());
    }
  };

  return (
    <Modal
      className="detail-view-modal"
      maskClosable={false}
      title={
        <div className="d-flex justify-content-start align-items-center">
          <Breadcrumb
            items={[
              {
                title: <HomeOutlined />,
              },
              {
                title: (
                  <span
                    className="parent-nav"
                    onClick={handleProjectBreadcrumbClick}
                  >
                    <FolderOutlined style={{ marginRight: 4 }} />
                    {projectName}
                  </span>
                ),
              },
              {
                title: reportName,
              },
            ]}
          />
          <Popconfirm
            placement="right"
            title={<></>}
            icon={<></>}
            showCancel={false}
            trigger="hover"
            okButtonProps={{
              style: {
                display: "none",
              },
            }}
            description={
              <div className="d-flex flex-column">
                <Descriptions
                  title=""
                  bordered
                  column={1}
                  size="small"
                  className="custom-description-title"
                >
                  <Descriptions.Item label="Created By">
                    {createdByName}
                  </Descriptions.Item>
                  {sourcePage === "pendingApproval" ? (
                    <>
                      <Descriptions.Item label="Pending Total Out">
                        ₹{(freshReportData?.userPendingTotalOut || extra?.userPendingTotalOut || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Pending Tax Amount">
                        ₹{(freshReportData?.userPendingTaxAmount || extra?.userPendingTaxAmount || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Pending Reimbursable Amount">
                        ₹{(freshReportData?.userPendingReimbursableAmount || extra?.userPendingReimbursableAmount || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Pending Reimbursable Tax Amount">
                        ₹{(freshReportData?.userPendingReimbursableTaxAmount || extra?.userPendingReimbursableTaxAmount || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Unclaimed Advance Amount">
                        ₹{(freshReportData?.userUnclaimedAdvanceAmount || extra?.userUnclaimedAdvanceAmount || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Pending Balance">
                        ₹{(freshReportData?.userPendingBalance || extra?.userPendingBalance || 0).toLocaleString()}.00
                      </Descriptions.Item>
                    </>
                  ) : (
                    <>
                      <Descriptions.Item label="Amount to be Reimbursed">
                        ₹{(freshReportData?.userReimbursableBalance || extra?.userReimbursableBalance || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Total In">
                        ₹{(freshReportData?.userTotalIn || extra?.userTotalIn || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Total Out">
                        ₹{(freshReportData?.userTotalOut || extra?.userTotalOut || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Balance">
                        ₹{(freshReportData?.userBalance || extra?.userBalance || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Non-reimbursable Amount">
                        ₹{(freshReportData?.userNonReimbursableAmount || extra?.userNonReimbursableAmount || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Reimbursable Tax Amount">
                        ₹{(freshReportData?.userReimbursableTaxAmount || extra?.userReimbursableTaxAmount || 0).toLocaleString()}.00
                      </Descriptions.Item>
                      <Descriptions.Item label="Total Advances">
                        ₹{(freshReportData?.userTotalAdvances || extra?.userTotalAdvances || 0).toLocaleString()}.00
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </div>
            }
          >
            <Icon
              name="info"
              className="ms-1"
              style={{
                cursor: "pointer",
              }}
            />
          </Popconfirm>
        </div>
      }
      style={{ top: 8 }}
      width={"99%"}
      styles={{
        body: {
          maxHeight: "calc(100vh - 120px)",
          overflowY: "auto",
          paddingBottom: "32px",
        },
      }}
      animation={false}
      open={open}
      footer={null}
      onCancel={() => {
        dispatch(updateRootPageRenderState(!RootPageRenderState));
        dispatch(resetReportInDetailModalSlice());
        window.dispatchEvent(new CustomEvent('reportBreakDownClosed'));
      }}
    >
      <ReportBreakDown 
        report={reportData} 
        onBack={() => {
          dispatch(updateRootPageRenderState(!RootPageRenderState));
          dispatch(resetReportInDetailModalSlice());
          window.dispatchEvent(new CustomEvent('reportBreakDownClosed'));
        }}
        projectName={projectName}
        isModalView={true}
        refreshData={refreshReportBreakDown}
        onRefreshComplete={handleRefreshComplete}
        projectId={projectId || undefined}
        sourcePage={sourcePage}
        allowEdit={ReportInDetailModal?.allowEdit || false}
        onFreshDataUpdate={handleFreshDataUpdate}
      />
    </Modal>
  );
};

export default ReportInDetailModal;
